/**
 * Runtime configuration. The API lives on the main domain; the player on
 * play.<domain>. Everything can be overridden with query parameters, which is
 * how the Pi agent, Electron and Android wrappers pass their settings.
 */
export interface PlayerConfig {
  apiUrl: string;
  realtimeUrl: string;
  agentPort: number | null;
  agentKey: string | null;
  demo: boolean;
  demoVariant: 'landscape' | 'portrait';
  previewScreenId: string | null;
  previewToken: string | null;
  /** Time compression for automated tests (durations are divided by this). */
  speed: number;
  /** Token passed in the URL path (/play/<token>) for devices without keyboard. */
  pathToken: string | null;
  debug: boolean;
  /** Render a single widget from an inline config (portal preview). */
  widgetPreview: {
    type: string;
    config: Record<string, unknown>;
    locale: 'nl' | 'en';
    timeZone: string;
  } | null;
}

function deriveApiUrl(loc: Location): string {
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL;
  const { protocol, hostname, port } = loc;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return `${protocol}//${hostname}:3000`;
  if (hostname.startsWith('play.')) return `${protocol}//${hostname.slice(5)}`;
  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
}

function deriveRealtimeUrl(loc: Location, apiUrl: string): string {
  if (import.meta.env.VITE_REALTIME_URL) return import.meta.env.VITE_REALTIME_URL;
  if (loc.hostname === 'localhost' || loc.hostname === '127.0.0.1')
    return `${loc.protocol}//${loc.hostname}:3002`;
  // In production Caddy routes /socket.io on the main domain to the realtime service.
  return apiUrl;
}

export function readConfig(loc: Location = window.location): PlayerConfig {
  const q = new URLSearchParams(loc.search);
  const apiUrl = (q.get('api') ?? deriveApiUrl(loc)).replace(/\/$/, '');
  const pathMatch = /^\/play\/(tbd_[A-Za-z0-9_-]{20,})/.exec(loc.pathname);
  const agentPort = q.get('agent');
  return {
    apiUrl,
    realtimeUrl: (q.get('rt') ?? deriveRealtimeUrl(loc, apiUrl)).replace(/\/$/, ''),
    agentPort: agentPort && /^\d{2,5}$/.test(agentPort) ? Number(agentPort) : null,
    agentKey: q.get('agentKey'),
    demo: q.get('demo') === '1',
    demoVariant: q.get('variant') === 'portrait' ? 'portrait' : 'landscape',
    previewScreenId: q.get('preview'),
    previewToken: q.get('pt'),
    speed: Math.min(1000, Math.max(1, Number(q.get('speed') ?? 1) || 1)),
    pathToken: pathMatch?.[1] ?? null,
    debug: q.get('debug') === '1',
    widgetPreview: parseWidgetPreview(q.get('widget')),
  };
}

function parseWidgetPreview(raw: string | null): PlayerConfig['widgetPreview'] {
  if (!raw) return null;
  try {
    const json = JSON.parse(
      decodeURIComponent(escape(atob(raw.replace(/-/g, '+').replace(/_/g, '/')))),
    ) as { type: string; config: Record<string, unknown>; locale?: string; timeZone?: string };
    return {
      type: json.type,
      config: json.config ?? {},
      locale: json.locale === 'en' ? 'en' : 'nl',
      timeZone: json.timeZone ?? 'Europe/Amsterdam',
    };
  } catch {
    return null;
  }
}

export const PLAYER_VERSION: string =
  typeof __PLAYER_VERSION__ === 'string' ? __PLAYER_VERSION__ : 'dev';
